// The Overview home's activity feed — the account's recent activity, newest first, as one list of rows.
//
// No time window: the list is the most recent rows, whatever their age. It is capped by row count
// (`feedRowCap`), not by a span, so a quiet account still sees its last few things rather than an
// empty list.
//
// Import-free of the app's dependency graph on purpose (see orion-web/CLAUDE.md → Tests): the screen,
// its filter chips, its counts and its summary numbers all read this, and a jest test has to be able to
// exercise the whole model without dragging common-lib in. Inputs are declared STRUCTURALLY here rather
// than imported from `lib/tasks` for the same reason — `WebTask` satisfies them.
//
// One row per THING, never one row per fact about a thing. A task the agent worked on twice is one row,
// which is what keeps "3 are moving now" and the "In progress (3)" chip the same number as the rows on
// screen. The agent activity log is not a row source: a note about what an agent did is not a task, and
// product chose not to list it on its own (2026-10-21).

import { awaitsDecision, bucketOfTask, isInMotion, isNotStarted, type AgentTaskBucket, type BucketableTask } from './agent-task-buckets';
import { stageGroup, type OutreachStage } from './outreach-stage';
import { OUTREACH_CARD_STATUS, outreachCardPerson, outreachCardStatus, outreachCardSummary, type OutreachCardStatus, type OutreachCardSummary } from './outreach-card';
import { BUCKET_BADGE, taskRowBadge, taskStatusBadge, type TaskStatusBadge } from './task-status';
import { gatingBlocker, isScheduledTask } from './task-schedule';
import { routineIsOn, routineToggleEnabled } from './routine-status';

/**
 * The DAILY SUMMARY card's window — 24 hours, while the feed above has no window at all.
 *
 * The card is titled "Daily Summary" and its headline opens "Overnight your team closed…", so it is a
 * statement about a DAY; pointing it at the whole feed would make that sentence false. The handoff's
 * "one source of truth" rule is still kept — `summaryNumbers` is the only place either number is
 * computed — it is now computed over the day rather than over everything the list shows. The feed's
 * chips and the card are answering two different questions and say so in their own words.
 */
export const HOME3_SUMMARY_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * What the feed can say right now.
 *
 * **"Could not read" and "there are none" are different facts**, and this screen is not allowed to
 * print the second when the first is true — an empty feed under a failed read tells the reader their
 * agents did nothing, which is the defect `/api/outreaches`' refusal to answer an empty list exists to
 * prevent (orion-web/CLAUDE.md → Data fetching). A quiet-day summary card reading "nothing closed and
 * nothing moved" over a 503 is the same lie with a friendlier voice.
 */
export type Home3ReadState = 'loading' | 'error' | 'ready';

/** One read's outcome, in the shape every one of this screen's hooks already reports. */
export type Home3ReadOutcome = { loading: boolean; error: boolean };

/**
 * The feed's state from its reads.
 *
 * The two reads that can PRODUCE a row decide it; apps are excluded on purpose — an app is a rare row
 * and one read per Space, so holding the whole feed on the slowest Space would put it in front of
 * every other row. An apps failure therefore costs the Apps chip its count and nothing else, which is
 * why it is reported separately rather than folded in here.
 *
 * Error wins over loading: a refetch after a failure is still a screen that has nothing trustworthy to
 * show, and flipping to a spinner would hide the failure it is retrying.
 */
export function resolveFeedState(tasks: Home3ReadOutcome): Home3ReadState {
  if (tasks.error) return 'error';
  if (tasks.loading) return 'loading';
  return 'ready';
}

/**
 * The "All" list's state. The feed does not wait for each category's own read (goals, routines, files,
 * apps), so those rows join a moment later — but an EMPTY list has to wait for all of them, or a slow or
 * failed read would read as an empty account.
 */
export function allListState(base: Home3ReadState, itemReads: Home3ReadOutcome[], feedIsEmpty: boolean): Home3ReadState {
  if (base !== 'ready' || !feedIsEmpty) return base;
  if (itemReads.some((read) => read.error)) return 'error';
  if (itemReads.some((read) => read.loading)) return 'loading';
  return 'ready';
}

/**
 * The state axis — tier 1 of the filter row, and the only thing a row's `state` can be.
 *
 * It IS `AgentTaskBucket`, deliberately not a fourth vocabulary: every count that fronts a tab in this
 * app comes from `bucketContaining`, and a feed that bucketed rows its own way would disagree with the
 * island, the Tasks tabs and the workspace stat cards about the same task.
 */
export type Home3State = AgentTaskBucket;

/** The tier-1 chips, in order. `failed` is absent — it joins conditionally, see `visibleStates`. */
export const HOME3_STATES: readonly Home3State[] = ['waiting', 'inProgress', 'done'] as const;

/** Tier 2 — where the row lives, i.e. which screen "See all …" would open. */
export type Home3Destination = 'tasks' | 'goals' | 'routines' | 'files' | 'apps' | 'outreach';

export const HOME3_DESTINATIONS: readonly Home3Destination[] = ['tasks', 'goals', 'routines', 'files', 'apps', 'outreach'] as const;

/**
 * The row's second line, before the Space: WHO did it.
 *
 * Every row but a message names its agent — its dot and its name, "● Ava | Daily Life". A row whose
 * payload names no agent (a task Maestro's own worker ran, a routine, a goal, an app) is Maestro's, and
 * wears Maestro's mark. A task NO agent has been put on yet names nobody (`none`) and says "Not
 * assigned yet". A MESSAGES row names the person instead, with the channel after them.
 */
export type Home3Subtitle =
  | { kind: 'agent'; agentId: string }
  | { kind: 'maestro' }
  | { kind: 'none' }
  /**
   * A MESSAGES row's second line: who the thread is with, then the channel — "Noa Levi | WhatsApp".
   * The title above it is the last thing said, so the person moves down here. `nameKey` replaces an
   * empty `name` for a sent thread whose recipient was never resolved. Either half can be empty.
   */
  | {
      kind: 'contact';
      name: string;
      nameKey?: string;
      channel: string;
      /**
       * A SENT thread's card line (`outreachCardSummary`): THEIR latest words, else what our AI sent labelled as
       * ours, else "No reply yet". Present → the row is titled by the PERSON and this is its second line.
       */
      summary?: OutreachCardSummary;
    };

export interface Home3Event {
  id: string;
  /** When it happened, ms. Rows are newest first. */
  at: number;
  /**
   * When the row FIRST appeared, ms — a different clock from `at`, and the one the tiles' unread
   * discs count against.
   *
   * `at` is the row's LAST activity, which is what orders the list and what its time column shows. A
   * running task keeps moving it, so counting "new since your last visit" against `at` made the disc
   * come back minutes after the reader cleared it, every time the agent took another step on work
   * they had already looked at (reported). Progress on something already seen is not a new update.
   *
   * For an app the two are the same instant — an app row is created and never moves.
   *
   * ⚠️ **ABSENT when the payload carries no usable creation date, and such a row never rings.** It
   * used to fall back to `at`, which quietly reinstated the whole bug for that row: `at` keeps
   * moving, so the fallback was recomputed on every rebuild and the disc came back anyway (review).
   * There is no stable first-seen time to be had for an undated row, so the honest answer is that we
   * cannot tell whether it is new — a missed disc, not one that refuses to stay cleared. `/api/tasks`
   * sends `createdDate` on every row (`isoOrEmpty`), so this is the degenerate case, not the norm.
   */
  firstAt?: number;
  /**
   * What a MESSAGES row opens — carried on the event because the row itself is not a task and the
   * feed's generic detail has nothing useful to show for one.
   *
   * Without it a received message opened a card holding its date, channel and badge and nothing else:
   * no correspondence, no reply, and a shared-file notice with no way to open the file (review, P1).
   * Both arms point at a drill-in that already exists, so this adds a route rather than a surface:
   *
   * - `inbound` hands `OutreachThreadModal` the very `WebTask` the route built, untouched — which is
   *   where `outreachMessages`, `sharedFile` and `rowKind` live;
   * - `sent` hands `TaskSentOutreachSection` the summary, the same correspondence the task detail
   *   shows for a thread the agent sent.
   *
   * Typed structurally rather than against `WebTask`/`SentOutreachSummary` so this module keeps its
   * no-runtime-imports rule; the call sites hold the real types.
   */
  outreach?: { kind: 'inbound'; task: unknown } | { kind: 'sent'; thread: unknown };
  /** What happened, in the user's own terms. NEVER a channel or an agent name — that is the subtitle. */
  title: string;
  /**
   * An i18n key to render INSTEAD of `title`, for the one row whose subject has no words of its own:
   * a sent thread whose recipient was never resolved. The model holds a key rather than English
   * because it is import-free of the app's i18n, and the row translates it.
   */
  titleKey?: string;
  state: Home3State;
  destination: Home3Destination;
  subtitle: Home3Subtitle;
  /**
   * The agent's name as the item itself records it — set on file rows only. A file's attribution can be a
   * writer KIND (`MAESTRO`, `SUB_AGENT`) rather than a team agent's id, so the roster lookup misses it and
   * the row needs the name the Files screen already resolves.
   */
  agentName?: string;
  /**
   * The badge this row wears — its OWN status, resolved through the shared `taskStatusBadge`, not the
   * filter chip's label. A failed task shows "Failed" in red and a cancelled one shows "Cancelled" even
   * while both sit under the same chip, because the chip is a question and the badge is the answer.
   */
  badge: TaskStatusBadge;
  /** A dated task held for the future (`isScheduledTask`): its `notBefore`, ISO. The row renders the
   *  date as the badge text, the way the Tasks list's LATER row does; `badge` then carries the variant. */
  scheduledAt?: string;
  /** A task waiting for another (`gatingBlocker`): that task's title. The row renders "After {title}". */
  afterTitle?: string;
  /** The task this row opens, when it is a task. Absent on an app row. */
  taskId?: string;
  /** The category item this row IS, when it is one — see `Home3ItemRef`. */
  item?: Home3ItemRef;
  /** A GOAL row's progress, 0–100 — the bar beside its badge. Absent on every other row. */
  progress?: number;
  /**
   * A ROUTINE row's on/off switch, beside its badge. `enabled` is false while the ACCOUNT paused it
   * (credits, a disconnected app): writing `paused: false` would be undone by the server, so the switch
   * shows the state and stays locked — the same rule as the Routines screen (`routineToggleEnabled`).
   */
  toggle?: { on: boolean; enabled: boolean };
  /** A GOAL row's tasks that have no row of their own (see `buildHome3Feed`). The brief card still counts them. */
  foldedTasks?: { at: number; state: Home3State }[];
  /**
   * Which Space the row belongs to, when the source says so.
   *
   * The design's second line reads "Email | Pizza Milano" — the how, then the Space. Only TASKS and
   * APPS carry a workspace. The NAME is
   * resolved at render from the workspaces the screen already reads — ids here, so the model stays
   * free of that lookup.
   */
  workspaceId?: string;
}

/**
 * A row that stands for one of the category's own ITEMS, not for an event: the goal, the routine, the
 * file itself. The row opens that item's own drill-in.
 *
 * They make a category list what is IN it, not only the tasks that point at it. An item row is dated by
 * the item's own last change, so it sorts with everything else and an old goal still shows. Mirrors
 * native `ItemRef`.
 */
export interface Home3ItemRef {
  kind: 'goal' | 'routine' | 'file';
  id: string;
}

/** A goal the account holds. The CALLER decides which goals count — the top-level list the Goals screen
 *  shows — so the model never re-derives that rule. `WebGoal` satisfies this. */
export interface Home3GoalInput {
  id: string;
  title?: string;
  workspaceId?: string;
  /** OPPORTUNITY / KICKOFF / ACTIVE / ACHIEVED / ABANDONED, as the service spells it. */
  status?: string;
  updatedDate?: string;
  /** Task-derived progress, 0–100, as the Goals screen shows it. */
  percent?: number;
  /** Its member goals. Their tasks fold into this goal's row, as their progress does into `percent`. */
  members?: { id: string }[];
}

/** A routine the account holds. `WebRoutine` satisfies this. */
export interface Home3RoutineInput {
  id: string;
  name?: string;
  workspaceId?: string;
  /** The agent that last did work for it. Not an owner — routines have none. */
  agentId?: string;
  lastRun?: string | null;
  createdDate?: string;
  /** OUT_OF_CREDITS / CONNECTOR_DISCONNECTED / PAUSED_BY_USER / … */
  pauseReason?: string | null;
  /** The three pause flags the toggle reads — see `routineIsOn` / `routineToggleEnabled`. */
  paused?: boolean;
  pausedOutOfCredits?: boolean;
  pausedConnectorDisconnected?: boolean;
  /** The routine has ended (lib/routine-end): its row has no switch. */
  ended?: boolean;
}

/** A file in one of the account's Spaces. Apps are not files here — they have their own category. */
export interface Home3FileInput {
  /** The file's stable key, built by the caller (`homeFileKey`). */
  id: string;
  name?: string;
  workspaceId?: string;
  /** The writer as `builtBy` records it — usually its KIND, not an agent id (see `fileSubtitle`). */
  agentId?: string;
  /** Who made it, as the Files screen names them (`creatorLabel`). */
  agentName?: string;
  updatedDate?: string;
  createdDate?: string;
}

/** A file's stable key in this feed: its Space, then its own id or path. Native builds the same shape. */
export function homeFileKey(file: { workspaceId?: string; fileId?: string; name: string }): string {
  return `${file.workspaceId ?? ''}:${file.fileId || file.name}`;
}

/** A task, as far as the feed is concerned. `WebTask` satisfies this. */
export interface Home3TaskInput extends BucketableTask {
  digestId?: string;
  /** Which Space the task belongs to. Absent on an account-wide read that did not say. */
  workspaceId?: string;
  id?: string;
  title?: string;
  subtitle?: string;
  currentAction?: string;
  agentId?: string;
  createdDate?: string | Date;
  lastActivityAt?: string;
  /** The routine whose firing raised it. */
  pulseIdentityId?: string;
  /** The goal whose plan laid it out. */
  goalId?: string;
  /**
   * Outreaches the task SENT — each carries the channel it went out on, and its own id so the sent
   * threads read separately (`Home3SentInput`) can skip one that is already folded onto a task row.
   */
  sentOutreaches?: { id?: string; channel?: string }[];
  /** An inbound outreach row, which is correspondence rather than a task the agent parked. */
  rowKind?: 'outreach';
}

/** An app an agent built. `WorkspaceApp` satisfies this. */
export interface Home3AppInput {
  id: string;
  workspaceId?: string;
  name?: string;
  description?: string;
  createdDate?: string;
}

function ms(value: string | Date | undefined): number | null {
  if (!value) return null;
  const parsed = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/** When a task last did anything the feed can date it by. */
function taskAt(task: Home3TaskInput): number | null {
  return ms(task.lastActivityAt) ?? ms(task.updatedDate) ?? ms(task.createdDate);
}

/**
 * Which channel a sent outreach went out on, as a display word. The payload spells them
 * `EMAIL` / `SMS` / `WHATSAPP` / `TELEGRAM` / `APP`; the row wants "Email", not a constant.
 */
const UNSET_CHANNEL = /^(UNKNOWN|N$)|UNSPECIFIED/;

export function channelLabel(channel: string | undefined): string {
  const raw = (channel ?? '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();
  // An unresolved channel is the service's own placeholder, never a word to show the reader.
  if (UNSET_CHANNEL.test(upper)) return '';
  if (upper === 'SMS') return 'SMS';
  if (upper === 'WHATSAPP') return 'WhatsApp';
  return upper.charAt(0) + upper.slice(1).toLowerCase();
}

/**
 * Where a task belongs, by precedence: **outreach, goal, routine, tasks.**
 *
 * Outreach wins because it is the only one of the four that changes the row's SECOND LINE as well as its
 * chip — a task that messaged someone says which channel it used, which is the most concrete thing the
 * row can say about itself, and burying it under "Goals" would leave the row saying nothing the title
 * did not already.
 *
 * **A task is never a Files row**, even when it produced files. The Files category lists the files
 * themselves (file name, then agent | Space — Figma 20369:30176), and every file a task wrote is already
 * one of those rows.
 */
export function destinationOf(task: Home3TaskInput): Home3Destination {
  if (task.sentOutreaches?.length || task.rowKind === 'outreach') return 'outreach';
  if (task.goalId) return 'goals';
  if (task.pulseIdentityId) return 'routines';
  return 'tasks';
}

/** The acting agent when the payload names one, Maestro otherwise. A blank id would draw a dot for nobody. */
function agentSubtitle(agentId: string | undefined): Home3Subtitle {
  const id = (agentId ?? '').trim();
  return id ? { kind: 'agent', agentId: id } : { kind: 'maestro' };
}

function taskRow(task: Home3TaskInput, at: number, now: number): Home3Event | null {
  const title = (task.title ?? '').trim();
  const id = (task.digestId ?? task.id ?? '').trim();
  // A row with no title has nothing to say and no way to be opened — dropped rather than titled
  // "Untitled", which is the same call `taskGoalOrigin` makes for a goal it cannot name.
  if (!title || !id) return null;
  const destination = destinationOf(task);
  return {
    id: `task:${id}`,
    at,
    // No fallback to `at` — see the field's own note. Absent means "cannot tell", which the count
    // reads as not-new.
    ...(() => {
      const createdAt = ms(task.createdDate);
      return createdAt === null ? {} : { firstAt: createdAt };
    })(),
    title,
    state: bucketOfTask(task),
    destination,
    // A task no agent was ever dispatched on has nobody to name — Maestro included. `agentConversationId`
    // is written at dispatch and kept after the run ends, so it is the durable "someone worked on this".
    subtitle: !(task.agentId ?? '').trim() && !task.agentConversationId && task.runInFlight !== true ? { kind: 'none' } : agentSubtitle(task.agentId),
    badge: home3Badge(
      // The activity log is where "what is happening right now" is read, so it is the surface that most
      // needs the Working/Active Task split. One argument, so no rule of the five can be dropped here.
      taskRowBadge(task, { now }),
    ),
    // The two quiet states the shared rule derives beside not-started. Same precedence as every other
    // badge site: a question at the reader beats both, scheduled beats gated. This screen used to badge a
    // task held for Sep 28 "In progress" — the row's status is PENDING and nothing here asked the rule.
    ...(!awaitsDecision(task) && isScheduledTask(task, now) && task.notBefore ? { scheduledAt: String(task.notBefore) } : {}),
    ...(!awaitsDecision(task) && !isScheduledTask(task, now) && gatingBlocker(task, now) ? { afterTitle: gatingBlocker(task, now)!.title } : {}),
    ...(task.workspaceId ? { workspaceId: task.workspaceId } : {}),
    taskId: id,
  };
}

/**
 * One received message, as a Messages row.
 *
 * ⚠️ **`firstAt` is the message's own time, deliberately — unlike a task's.** A task's `at` moves as
 * an agent works on it, which is why counting the unread disc against it re-rang the tile on work
 * already seen. A reply is the opposite: the row IS the news, and a newer reply is newer news. So
 * each message landing is a thing the reader has genuinely not seen, and the disc should say so.
 *
 * The title is the LAST THING SAID, and the second line is who said it and where — "Noa Levi |
 * WhatsApp" (`messageTitle`). A row with no sender has nobody to attribute it to and is dropped, the
 * same call a task row makes for a task with no title.
 */
function receivedRow(received: Home3ReceivedInput, at: number, stage: OutreachStage | undefined): Home3Event | null {
  const name = (received.senderName ?? '').trim();
  if (!name || !received.id) return null;
  return {
    id: `received:${received.id}`,
    at,
    firstAt: at,
    ...messageTitle(received.summary, name, undefined, received.channel),
    // The STAGE when the contact book has this thread (`stageByOutreachId`); a miss keeps today's rule.
    ...(stage ? stageStateAndBadge(stage) : {
      state: bucketOfTask({ status: received.status ?? '' } as Home3TaskInput),
      badge: home3Badge(taskStatusBadge(received.status ?? '')),
    }),
    destination: 'outreach',
    // No `taskId` — there is no task behind an inbound message. It opens the OUTREACH thread instead,
    // which is where the correspondence, the reply and a shared file's own link live.
    ...(received.task ? { outreach: { kind: 'inbound' as const, task: received.task } } : {}),
  };
}

/**
 * A Messages row's title and second line: the last thing said is the title, the person and the
 * channel are the second line.
 *
 * A thread with no text to show (a media-only message, a payload that sent none) is titled by the
 * person instead, and the second line then carries only the channel — never the name twice.
 */
function messageTitle(
  said: string | undefined,
  name: string,
  nameKey: string | undefined,
  rawChannel: string | undefined,
): Pick<Home3Event, 'title' | 'titleKey' | 'subtitle'> {
  const text = (said ?? '').trim();
  const channel = channelLabel(rawChannel);
  if (text) {
    return { title: text, subtitle: { kind: 'contact', name, ...(nameKey ? { nameKey } : {}), channel } };
  }
  return {
    title: name,
    ...(nameKey ? { titleKey: nameKey } : {}),
    subtitle: { kind: 'contact', name: '', channel },
  };
}

/** The last entry on a thread that carries a date, with whether it was theirs and what it said. */
function lastSentEntry(thread: Home3SentInput): { at: number; mine: boolean; text: string } | null {
  const dated = (thread.messages ?? []).map((m) => ({ at: ms(m.at), mine: m.mine === true, text: m.text ?? '' })).filter((m) => m.at !== null);
  if (dated.length === 0) return null;
  // Oldest first by contract, but sorted rather than trusted — a thread that arrives out of order
  // would otherwise date the row by the wrong end of the conversation.
  return dated.sort((a, b) => (a.at as number) - (b.at as number)).at(-1) as { at: number; mine: boolean; text: string };
}

/**
 * The title for a sent thread whose recipient the sender's own assistant never resolved.
 *
 * The same key the task detail's sent-thread section falls back to, so the two surfaces name an
 * unnamed recipient identically. Dropping the row instead loses a message the user actually sent —
 * on the account this was measured against, one of three live WhatsApp threads carried no
 * `recipientName` and so was the one thread still missing from the category.
 */
export const HOME3_UNNAMED_RECIPIENT_KEY = 'tasks.sentOutreach.unnamedRecipient';

/**
 * One SENT correspondence, as a Messages row.
 *
 * Dated by the last thing said, **whoever said it**, so a reply lifts the thread rather than leaving
 * it where the agent's own message put it. `firstAt` is that same instant, deliberately and for the
 * same reason an inbound row's is: the newest message IS the news.
 *
 * The state says what the reader has to do about it: still waiting on them, or answered.
 */
function sentRow(thread: Home3SentInput, last: { at: number; mine: boolean; text: string }, stage: OutreachStage | undefined): Home3Event | null {
  if (!thread.id) return null;
  // The PERSON titles the row (Ronny, 2026-10-22: "Messages · <our whole opening message>" over "Recipient | App"
  // said neither who it was nor whether it needed him). The second line is the channel and THEIR latest words.
  const person = outreachCardPerson({ recipientName: thread.recipientName, contactName: thread.contactName, leadName: thread.leadName });
  const messages = (thread.messages ?? []).map((m) => ({ mine: m.mine === true, text: m.text, at: m.at }));
  const status = outreachCardStatus({
    messages,
    stageGroup: stage ? stageGroup(stage) : null,
    leadNeedsYou: thread.leadNeedsYou,
    awaitingReply: thread.awaitingReply,
  });
  return {
    id: `sent:${thread.id}`,
    at: last.at,
    firstAt: last.at,
    title: person,
    // Nobody named them — never "Recipient".
    ...(person ? {} : { titleKey: HOME3_UNNAMED_PERSON_KEY }),
    subtitle: { kind: 'contact', name: '', channel: channelLabel(thread.channel), summary: outreachCardSummary(messages) },
    ...cardStateAndBadge(status),
    destination: 'outreach',
    ...(thread.thread ? { outreach: { kind: 'sent' as const, thread: thread.thread } } : {}),
  };
}

/** The fallback a SENT row's person takes when nobody named them. */
export const HOME3_UNNAMED_PERSON_KEY = 'contacts.row.unnamed';

/** A card status as the feed's state bucket and badge — the label is the status's own words. */
function cardStateAndBadge(status: OutreachCardStatus): Pick<Home3Event, 'state' | 'badge'> {
  const view = OUTREACH_CARD_STATUS[status];
  return { state: view.bucket, badge: { labelKey: view.labelKey, variant: view.variant } };
}

/**
 * A Messages row's state and badge from its thread's STAGE group — Needs you · In progress · Done — the
 * same three groups, words and colours the Outreach screen draws. The badge label is the group's own key.
 */
function stageStateAndBadge(stage: OutreachStage): Pick<Home3Event, 'state' | 'badge'> {
  switch (stageGroup(stage)) {
    case 'needsYou':
      return { state: 'waiting', badge: { labelKey: 'contacts.detail.needsYou', variant: 'waiting-on-you' } };
    case 'done':
      return { state: 'done', badge: { labelKey: 'tasks.status.done', variant: 'done' } };
    default:
      return { state: 'inProgress', badge: { labelKey: 'tasks.status.inProgress', variant: 'in-progress' } };
  }
}

function appRow(app: Home3AppInput, at: number): Home3Event | null {
  const title = (app.name ?? '').trim();
  if (!title) return null;
  return {
    id: `app:${app.id}`,
    at,
    // An app row is dated by its creation and does not move.
    firstAt: at,
    title,
    state: 'done',
    destination: 'apps',
    // The app record carries no author, so every app row is Maestro's until the server says otherwise.
    subtitle: { kind: 'maestro' },
    badge: home3Badge(taskStatusBadge('COMPLETED')),
    ...(app.workspaceId ? { workspaceId: app.workspaceId } : {}),
  };
}

/**
 * A message somebody SENT the account — a reply on WhatsApp, an SMS, an email.
 *
 * It is NOT a task, which is why it needs its own input: a received outreach is the other person's
 * correspondence, and `/api/tasks` deliberately stopped folding inbound rows into the task list (map
 * home iteration 3). The Overview home read that list and nothing else, so a reply to an outreach the
 * agent had sent appeared nowhere on the screen — the Messages tile showed what WAS SENT and never
 * what came back (reported). This is the fourth read that closes that.
 *
 * The payload is the one `/api/outreaches` already returns for the task list's sent-side fold, so
 * nothing new is fetched to show these.
 */
export interface Home3ReceivedInput {
  id: string;
  /** The row the route built, passed straight through to the thread drill-in. */
  task?: unknown;
  /** Who wrote — the row's title. */
  senderName?: string;
  /** What they said, trimmed by the route. The row's second line, after the channel. */
  summary?: string;
  /** `WHATSAPP` / `SMS` / `EMAIL` / … as the service spells it; the row says it in words. */
  channel?: string;
  /** When the last message on the thread landed. */
  updatedDate?: string | Date;
  status?: string;
}

/**
 * A thread the account SENT — and the reply on it, when one came back.
 *
 * ⚠️ **A reply is NOT a received outreach.** This was the second half of the same report, and the
 * part the first fix got wrong: `received` holds outreaches somebody else INITIATED, while an answer
 * to a message the agent sent lands as a message INSIDE the sent thread (`replied: true`, and a last
 * entry with `mine: false`). Measured on a live account: two WhatsApp messages sent that morning and
 * one reply, all three in `sent`, and `received` holding only older threads other people had started.
 *
 * It also cannot come through the task fold. A sent outreach is folded onto the task that raised it,
 * and a message sent from a CHAT has no such task — on that same account, nine tasks and not one
 * carrying a `sentOutreaches` entry, so the Messages category was empty with three live threads in
 * the payload.
 *
 * So the thread is its own row: one per correspondence, titled by the person, dated by the LAST thing
 * said on it either way, so a reply lifts it to the top of the board.
 */
export interface Home3SentInput {
  id: string;
  /** The summary itself, passed through to the thread drill-in. */
  thread?: unknown;
  /** Who it went to — the row's title. */
  recipientName?: string;
  /** APP / WHATSAPP / SMS / EMAIL / TELEGRAM, as the service spells it. */
  channel?: string;
  /** Did they ever write back? */
  replied?: boolean;
  /** Still open and waiting on THEM? */
  awaitingReply?: boolean;
  /** The correspondence, oldest first. The row is dated by the last entry that carries a date. */
  messages?: { text?: string; at?: string; mine?: boolean }[];
  /** The contact book's name for the person, when it holds the thread. */
  contactName?: string;
  /** The profile-link lead's name for an unnamed visitor ("Visitor from Wix 3.0", `leadDisplayName`). */
  leadName?: string;
  /** The lead has an open decision for the owner (`leadStatus` = needs_you). */
  leadNeedsYou?: boolean;
}

export interface Home3FeedInputs {
  tasks?: Home3TaskInput[];
  apps?: Home3AppInput[];
  received?: Home3ReceivedInput[];
  sent?: Home3SentInput[];
  goals?: Home3GoalInput[];
  routines?: Home3RoutineInput[];
  files?: Home3FileInput[];
  /** The team roster. A file names its writer by NAME, not id, so this is how a file row finds its agent's dot. */
  agents?: { id: string; name: string }[];
  /** Injected, never read from the clock — a task row's scheduled/overdue wording reads it. */
  now: number;
  /**
   * Each outreach thread's STAGE, keyed by outreach id — the contact-book head joined once by the caller
   * (`useContactBook('')`, warmed on app mount). A Messages row whose thread is here takes its state and
   * badge from the stage; a miss keeps today's rule. Optional: no stage, no change.
   */
  stageByOutreachId?: ReadonlyMap<string, OutreachStage>;
}

/**
 * The feed: every task, app, message, goal, routine and file it is handed, newest first.
 * No time window.
 */
export function buildHome3Feed({
  tasks = [],
  apps = [],
  received = [],
  sent = [],
  goals = [],
  routines = [],
  files = [],
  agents = [],
  now,
  stageByOutreachId,
}: Home3FeedInputs): Home3Event[] {
  const rows: Home3Event[] = [];
  // Sent outreaches already folded onto a task row, so the thread is not listed twice.
  const foldedSentIds = new Set<string>();

  // A goal's tasks have no rows of their own: the goal's row stands for them.
  // Only a task whose goal is listed below folds, so a task is never lost when the goals read has not
  // answered or its goal is one the Goals list hides.
  const goalRowFor = new Map<string, string>();
  for (const goal of goals) {
    if (!goal.id) continue;
    for (const member of goal.members ?? []) if (member.id && !goalRowFor.has(member.id)) goalRowFor.set(member.id, goal.id);
    goalRowFor.set(goal.id, goal.id);
  }
  const folded = new Map<string, Home3Event[]>();

  for (const task of tasks) {
    const at = taskAt(task);
    if (at === null) continue;
    const row = taskRow(task, at, now);
    if (!row) continue;
    const goalId = task.goalId ? goalRowFor.get(task.goalId) : undefined;
    if (row.destination === 'goals' && goalId) {
      folded.set(goalId, [...(folded.get(goalId) ?? []), row]);
      continue;
    }
    rows.push(row);
    for (const outreach of task.sentOutreaches ?? []) if (outreach.id) foldedSentIds.add(outreach.id);
  }

  for (const app of apps) {
    const at = ms(app.createdDate);
    if (at === null) continue;
    const row = appRow(app, at);
    if (row) rows.push(row);
  }

  // Sent correspondence — one row per thread, EXCEPT the threads already folded onto a task row
  // above, which would otherwise appear twice.
  for (const thread of sent) {
    if (thread.id && foldedSentIds.has(thread.id)) continue;
    const last = lastSentEntry(thread);
    if (last === null) continue;
    const row = sentRow(thread, last, thread.id ? stageByOutreachId?.get(thread.id) : undefined);
    if (row) rows.push(row);
  }

  // Inbound messages — outreaches somebody else INITIATED. They carry no task id and are not deduped
  // against the task rows: an inbound outreach's `sourceRecipientTaskId` names a task in the SENDER's
  // metasite, not one of ours, so there is nothing on this side for it to double.
  for (const message of received) {
    const at = ms(message.updatedDate);
    if (at === null) continue;
    const row = receivedRow(message, at, message.id ? stageByOutreachId?.get(message.id) : undefined);
    if (row) rows.push(row);
  }

  // The category's own items. A goal's row also stands for its folded tasks (above).
  for (const goal of goals) {
    const row = goalRow(goal, folded.get(goal.id) ?? []);
    if (row) rows.push(row);
  }
  for (const routine of routines) {
    const at = ms(routine.lastRun ?? undefined) ?? ms(routine.createdDate);
    if (at === null) continue;
    const row = routineRow(routine, at);
    if (row) rows.push(row);
  }
  const agentIdByName = new Map<string, string>();
  for (const agent of agents) {
    const name = agent.name.trim().toLowerCase();
    if (name && agent.id && !agentIdByName.has(name)) agentIdByName.set(name, agent.id);
  }
  for (const file of files) {
    const at = ms(file.updatedDate) ?? ms(file.createdDate);
    if (at === null) continue;
    const row = fileRow(file, at, agentIdByName);
    if (row) rows.push(row);
  }

  // Ties break on id so two rows stamped the same millisecond keep one order across rebuilds — and
  // across the three surfaces, which sort the same way.
  return rows.sort((a, b) => b.at - a.at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/**
 * A goal's state: an OPPORTUNITY is waiting on the reader to take it up, a settled goal (achieved or
 * abandoned) is done, and everything else is still being worked. Abandoned is NOT failed — the reader or
 * the agent stopped it on purpose, and a Failed badge would call that an error.
 */
export function goalState(status: string | undefined): Home3State {
  switch ((status ?? '').toUpperCase()) {
    case 'OPPORTUNITY':
      return 'waiting';
    case 'ACHIEVED':
    case 'ABANDONED':
      return 'done';
    default:
      return 'inProgress';
  }
}

/**
 * A routine row stands for its LAST RUN, which has finished — so it is done. The exception is a routine
 * the system stopped and only the reader can restart (out of credits, a disconnected app): that one is
 * waiting on them. A routine the reader paused is their own choice, so it is not.
 */
export function routineState(pauseReason: string | null | undefined): Home3State {
  const reason = (pauseReason ?? '').toUpperCase();
  return reason === 'OUT_OF_CREDITS' || reason === 'CONNECTOR_DISCONNECTED' ? 'waiting' : 'done';
}

/**
 * A goal's row, standing for its folded tasks too: dated by the newest of them, "new" when one of them is,
 * and waiting on the reader while one of them is — a goal row reading "In progress" over a task that needs
 * an answer would hide the question.
 */
function goalRow(goal: Home3GoalInput, tasks: Home3Event[]): Home3Event | null {
  const title = (goal.title ?? '').trim();
  if (!goal.id || !title) return null;
  const own = ms(goal.updatedDate);
  const times = [...(own === null ? [] : [own]), ...tasks.map((task) => task.at)];
  if (times.length === 0) return null;
  const at = Math.max(...times);
  const ownState = goalState(goal.status);
  const state = ownState === 'inProgress' && tasks.some((task) => task.state === 'waiting') ? 'waiting' : ownState;
  // A goal carries no creation date, and `updatedDate` moves — see `firstAt`. So it is "new" only through
  // the newest folded task's own first appearance.
  const firstAts = tasks.flatMap((task) => (task.firstAt === undefined ? [] : [task.firstAt]));
  return {
    id: `goal:${goal.id}`,
    at,
    title,
    state,
    destination: 'goals',
    subtitle: agentSubtitle(undefined),
    badge: home3StateBadge(state),
    item: { kind: 'goal', id: goal.id },
    ...(firstAts.length ? { firstAt: Math.max(...firstAts) } : {}),
    ...(tasks.length ? { foldedTasks: tasks.map((task) => ({ at: task.at, state: task.state })) } : {}),
    ...(typeof goal.percent === 'number' && Number.isFinite(goal.percent) ? { progress: Math.round(Math.min(100, Math.max(0, goal.percent))) } : {}),
    ...(goal.workspaceId ? { workspaceId: goal.workspaceId } : {}),
  };
}

function routineRow(routine: Home3RoutineInput, at: number): Home3Event | null {
  const title = (routine.name ?? '').trim();
  if (!routine.id || !title) return null;
  const state = routineState(routine.pauseReason);
  const firstAt = ms(routine.createdDate);
  return {
    id: `routine:${routine.id}`,
    at,
    ...(firstAt !== null ? { firstAt } : {}),
    title,
    state,
    destination: 'routines',
    subtitle: agentSubtitle(routine.agentId),
    badge: home3StateBadge(state),
    item: { kind: 'routine', id: routine.id },
    ...(routine.ended === true ? {} : { toggle: { on: routineIsOn(routine), enabled: routineToggleEnabled(routine) } }),
    ...(routine.workspaceId ? { workspaceId: routine.workspaceId } : {}),
  };
}

/**
 * Who a file row credits. `agentId` on a file is usually NOT an agent id: the harness stamps the writer's
 * KIND (`MAESTRO`, `SUB_AGENT`, `USER`, …) plus an optional name (`writerFor` in orion-harness-poc), so
 * the kind must never be hashed into a dot.
 *
 * - no writer, or `MAESTRO` (an unnamed `SUB_AGENT` already arrives as `MAESTRO`, via `creatorOf`) → Maestro.
 * - a named `SUB_AGENT` → the team agent with that name. Not on the roster (still loading, or renamed since)
 *   → the name with no dot, never a hue that belongs to nobody.
 * - any other kind (`USER`, `SYSTEM`, …) → the name with no dot.
 * - anything else is a real agent id, from a file header.
 */
function fileSubtitle(file: Home3FileInput, agentIdByName: Map<string, string>): Home3Subtitle {
  const writer = (file.agentId ?? '').trim();
  if (!writer || writer.toUpperCase() === 'MAESTRO') return { kind: 'maestro' };
  if (!ACTOR_KIND.test(writer)) return { kind: 'agent', agentId: writer };
  const name = writer === 'SUB_AGENT' ? (file.agentName ?? '').trim().toLowerCase() : '';
  return { kind: 'agent', agentId: (name && agentIdByName.get(name)) || '' };
}

/** A writer kind is an upper-case wire enum; a real agent id is a GUID. */
const ACTOR_KIND = /^[A-Z][A-Z_]*$/;

function fileRow(file: Home3FileInput, at: number, agentIdByName: Map<string, string>): Home3Event | null {
  // The name can be a path; the row reads the file's own name.
  const title = (file.name ?? '').split('/').pop()?.trim() ?? '';
  if (!file.id || !title) return null;
  const firstAt = ms(file.createdDate);
  return {
    id: `file:${file.id}`,
    at,
    ...(firstAt !== null ? { firstAt } : {}),
    title,
    state: 'done',
    destination: 'files',
    subtitle: fileSubtitle(file, agentIdByName),
    ...(file.agentName?.trim() ? { agentName: file.agentName.trim() } : {}),
    badge: home3StateBadge('done'),
    item: { kind: 'file', id: file.id },
    ...(file.workspaceId ? { workspaceId: file.workspaceId } : {}),
  };
}

/** The name on an agent row's second line: the team roster's, else the one the item recorded. */
export function home3AgentName(event: Home3Event, roster: Record<string, string>): string {
  if (event.subtitle.kind !== 'agent') return '';
  return roster[event.subtitle.agentId] || event.agentName || '';
}

/** How many distinct agents are behind these rows — the header's "across N agents". */
export function agentCount(events: Home3Event[]): number {
  const ids = new Set<string>();
  for (const event of events) if (event.subtitle.kind === 'agent' && event.subtitle.agentId) ids.add(event.subtitle.agentId);
  return ids.size;
}

/**
 * The tier-1 chips actually rendered: the three always-visible states, plus `failed` only when
 * something failed. Same rule (and same reason) as `visibleBuckets` on the Tasks screen — a Failed chip
 * reading (0) on every healthy account is a permanent invitation to an empty list.
 */
export function visibleStates(events: Home3Event[]): Home3State[] {
  return events.some((event) => event.state === 'failed') ? [...HOME3_STATES, 'failed'] : [...HOME3_STATES];
}

/** Which state the row filter is on. `null` is "All" — tier 1 always has exactly one value. */
export type Home3StateFilter = Home3State | null;
/** Which destination the row filter is on. `null` is "every destination" — tier 2 starts here. */
export type Home3DestinationFilter = Home3Destination | null;

/** Both tiers AND-ed. This is the one place the two combine; nothing else may filter a row. */
export function filterFeed(events: Home3Event[], state: Home3StateFilter, destination: Home3DestinationFilter): Home3Event[] {
  return events.filter((event) => (!state || event.state === state) && (!destination || event.destination === destination));
}

/**
 * A tier-1 chip's count, measured against the OTHER tier's current selection — so once "Tasks" is
 * picked, "Waiting on you" says how many TASKS are waiting rather than how many things overall are.
 * Recomputed per render against both values; there is deliberately nothing to cache.
 */
export function stateCounts(events: Home3Event[], destination: Home3DestinationFilter): Record<Home3State | 'all', number> {
  const scoped = filterFeed(events, null, destination);
  const counts = { all: scoped.length, waiting: 0, inProgress: 0, done: 0, failed: 0 };
  for (const event of scoped) counts[event.state] += 1;
  return counts;
}

/** A tier-2 chip's count, measured against tier 1's current selection. The mirror of `stateCounts`. */
export function destinationCounts(events: Home3Event[], state: Home3StateFilter): Record<Home3Destination, number> {
  const scoped = filterFeed(events, state, null);
  const counts: Record<Home3Destination, number> = { tasks: 0, goals: 0, routines: 0, files: 0, apps: 0, outreach: 0 };
  for (const event of scoped) counts[event.destination] += 1;
  return counts;
}

/**
 * Every destination's per-state breakdown — **the six category cards' badge clusters**.
 *
 * Unlike `stateCounts`/`destinationCounts` this reads NO current selection. The cards are the
 * destination control itself, so a card whose numbers shrank when it was picked would be a control
 * reporting on its own effect: the reader taps "Tasks · 6" and the card it just tapped says 2. The
 * state picker beside the list narrows the ROWS, and the cards keep telling the reader what is there
 * to go and look at.
 */
export function destinationStateCounts(events: Home3Event[]): Record<Home3Destination, Record<Home3State, number>> {
  const zero = (): Record<Home3State, number> => ({ waiting: 0, inProgress: 0, done: 0, failed: 0 });
  const counts = {
    tasks: zero(),
    goals: zero(),
    routines: zero(),
    files: zero(),
    apps: zero(),
    outreach: zero(),
  } satisfies Record<Home3Destination, Record<Home3State, number>>;
  for (const event of events) counts[event.destination][event.state] += 1;
  return counts;
}

/**
 * "N new updates since your last visit" — rows that landed after the reader last opened this home.
 *
 * `sinceMs` comes from `useNewSince('home')`, which is `lib/summary-freshness.ts`' rule and not one of
 * this screen's own: a first-ever visit falls back to the last 24 hours rather than announcing the
 * whole window as new, and a device whose clock ran ahead is ignored instead of silencing the line
 * forever. `null` means the stamp has not been read yet (the server has no localStorage), and the
 * caller renders nothing — never a zero, which would read as "nothing happened".
 */
export function newUpdates(events: Home3Event[], sinceMs: number): number {
  return events.filter((event) => event.at > sinceMs).length;
}

/**
 * The summary card's two numbers: how much closed, how much is still moving — **over the last DAY**,
 * whatever span the feed below is showing.
 *
 * ⚠️ **This is still the screen's ONLY source for them.** The rule it exists for is that no second copy
 * of "12" and "3" is computed anywhere else; it is not that the card and the chips must span the same
 * time. They no longer do, deliberately (see `HOME3_SUMMARY_WINDOW_MS`): the card reports the overnight
 * day it names, the chips count the list they front.
 */
export function summaryWindowEvents(events: Home3Event[], now: number): Home3Event[] {
  const since = now - HOME3_SUMMARY_WINDOW_MS;
  // Item rows are left out: the card counts work that closed or is moving, and a goal or a file the
  // account HOLDS is neither.
  return events.filter((event) => !event.item && event.at >= since);
}

/** A goal's folded tasks count as the rows they would have been. */
export function summaryNumbers(events: Home3Event[], now: number): { closed: number; moving: number } {
  const since = now - HOME3_SUMMARY_WINDOW_MS;
  const states = [
    ...summaryWindowEvents(events, now).map((event) => event.state),
    ...events.flatMap((event) => event.foldedTasks ?? []).filter((task) => task.at >= since).map((task) => task.state),
  ];
  return {
    closed: states.filter((state) => state === 'done').length,
    moving: states.filter((state) => state === 'inProgress').length,
  };
}

/** How many rows the list renders — three on a narrow pane, six on a wide one. */
export const HOME3_NARROW_CAP = 3;
export const HOME3_WIDE_CAP = 6;

/**
 * How many rows the list renders before any "Show more". Three on a narrow pane, six on a wide one,
 * because a stacked row is much taller than a compact one, so three of them occupy roughly what six do
 * side by side.
 *
 * The list grows in place from there, `HOME3_SHOW_MORE_STEP` rows per tap. A long list pushes the content
 * under it down, and product accepted that.
 */
export function feedRowCap(narrow: boolean): number {
  return narrow ? HOME3_NARROW_CAP : HOME3_WIDE_CAP;
}

/** How many more rows one "Show more" tap reveals. Shared 1:1 with iOS and Android. */
export const HOME3_SHOW_MORE_STEP = 5;

/** The rows on screen after `taps` presses of "Show more", never more than there are. */
export function visibleFeedRows(cap: number, taps: number, total: number): number {
  return Math.min(total, cap + taps * HOME3_SHOW_MORE_STEP);
}

/**
 * The control under the list's rows. ONE "Show more" tap reveals a step and turns the control into
 * "Show less", which folds the list back to the cap (Figma 21882-133464); every row beyond that is
 * "Show all"'s modal. Mirrors iOS `HomeFeed.revealControl` / Android `HomeFeed.revealControl`.
 */
export type FeedRevealControl = 'showMore' | 'showLess' | 'none';

export function feedRevealControl(visible: number, total: number, cap: number): FeedRevealControl {
  if (Math.min(visible, total) > cap) return 'showLess';
  return total > visible ? 'showMore' : 'none';
}

/** "Show all" beside the footnote is offered while the list on screen still hides rows. */
export function showsFeedShowAll(visible: number, total: number): boolean {
  return total > visible;
}

/**
 * The tier-1 chip labels — and the row badges', for the three states this screen names.
 *
 * ⚠️ **This screen says "Waiting on you" where the rest of the app says "Action Needed", and that is
 * the design's call, not drift.** It reads as the QUESTION this screen asks ("what is waiting on
 * me?") rather than as a task's stored status. The `variant` — the colour — still comes from the
 * shared `taskStatusBadge`, so a waiting row is the same orange here as everywhere else.
 *
 * `done` and `failed` keep the SHARED keys: "Completed" was this screen's own word for done and has
 * been retired in favour of the app's "Done" (`tasks.status.done`), which is already translated in all
 * 37 bundles — a second word for one state is how two screens come to disagree about the same task.
 * `inProgress` keeps its own key only because it exists; its English is identical to the shared one.
 */
export const HOME3_STATE_LABEL_KEY: Record<Home3State, string> = {
  waiting: 'home.overview.state.waiting',
  inProgress: 'home.overview.state.inProgress',
  done: 'tasks.status.done',
  failed: 'tasks.status.failed',
};

/**
 * The row badge's label, for the three states the design names — and ONLY those.
 *
 * Every other outcome (Paused, Failed, Cancelled, Retired, Not started, In review, an outreach's
 * Reply) passes through with the label `taskStatusBadge` gave it. Those are the rows whose status is
 * the exceptional one, and overwriting them with one of three friendly words is exactly how a
 * cancelled task comes to read as completed.
 *
 * Keyed on the shared label the badge arrived with rather than on the bucket, so an exception stays an
 * exception: `taskStatusBadge` returns the bucket's own key only when none of its special cases fired.
 */
const BADGE_LABEL_OVERRIDE: Record<string, string> = {
  // The shared waiting badge now arrives as `tasks.status.waitingOnYou` ("Waiting on you"); this screen
  // keeps its own key for the row badge (same words), so the Overview wording stays self-contained.
  'tasks.status.waitingOnYou': 'home.overview.badge.waiting',
  // The in-progress override is GONE on purpose. It existed so the Overview's wording stayed
  // self-contained, and the cost was that this screen kept one word ("In progress") for the two
  // states the shared badge now separates — so the row a reader had just released still read exactly
  // as it did before they pressed. The activity log is precisely where the distinction was asked for,
  // so it inherits the shared `Active Task` / `Working` instead of overriding them.
};

/** The badge as this screen draws it: the shared variant, the design's word for the three it names. */
export function home3Badge(badge: TaskStatusBadge): TaskStatusBadge {
  const labelKey = BADGE_LABEL_OVERRIDE[badge.labelKey];
  return labelKey ? { ...badge, labelKey } : badge;
}

/**
 * The badge a STATE wears wherever this screen names one — a category card's count, the picker's
 * options, a row.
 *
 * `BUCKET_BADGE` gives the shared variant (so `waiting` is the same orange here as on the Tasks
 * screen) and `home3Badge` puts this screen's own three words on it. ONE function, because the three
 * places that render a state used to hold three copies of the mapping and could drift apart in colour
 * while agreeing in text.
 */
export function home3StateBadge(state: Home3State): TaskStatusBadge {
  return home3Badge(BUCKET_BADGE[state]);
}

/** The tier-2 chip labels — the nav's own words for the six destinations, for the same reason. */
export const HOME3_DESTINATION_LABEL_KEY: Record<Home3Destination, string> = {
  tasks: 'home.map.bucket.tasks',
  goals: 'home.map.bucket.goals',
  routines: 'home.map.bucket.routines',
  files: 'home.map.bucket.files',
  apps: 'home.map.bucket.apps',
  // "Messages", the same word the map home's sixth tile uses — the category frames name it that, and a
  // tile reading "Outreach" beside one reading "Messages" is the app calling one thing two names.
  outreach: 'home.map.bucket.messages',
};

/**
 * The destination as a SINGULAR noun — the row's subject of last resort.
 *
 * Singular because it describes one row ("Task | Personal"), where `HOME3_DESTINATION_LABEL_KEY` names
 * the whole destination for a card or a chip ("Tasks"). Its own keys rather than the nav's, because
 * every language that inflects makes that a different word and reusing the plural would read wrong in
 * most of them.
 */
export const HOME3_SUBJECT_LABEL_KEY: Record<Home3Destination, string> = {
  tasks: 'home.overview.subject.task',
  goals: 'home.overview.subject.goal',
  routines: 'home.overview.subject.routine',
  files: 'home.overview.subject.file',
  apps: 'home.overview.subject.app',
  outreach: 'home.overview.subject.outreach',
};

/**
 * The zero-results line: which key, and what to interpolate.
 *
 * Both halves of the filter are named when they are set and omitted when they are not, so the sentence
 * says what produced the emptiness — "No waiting on you tasks activity…" rather than a blank card,
 * which is the one state this screen is not allowed to have. The labels are joined into ONE `{filters}`
 * placeholder so a translator can put it wherever the language wants it.
 */
export function emptyFeedMessage(state: Home3StateFilter, destination: Home3DestinationFilter): { key: string; filterKeys: string[] } {
  const filterKeys = [...(state ? [HOME3_STATE_LABEL_KEY[state]] : []), ...(destination ? [HOME3_DESTINATION_LABEL_KEY[destination]] : [])];
  return { key: filterKeys.length ? 'home.overview.emptyFiltered' : 'home.overview.empty', filterKeys };
}

/**
 * A row's clock time.
 *
 * The locale is left UNDEFINED on purpose — the browser's own locale is the user's chosen time format,
 * and overriding it with the account's region is the bug `test/home-date-browser-locale.test.ts` pins
 * for the header date. Same rule, same reason, one line down the page.
 */
export function formatFeedTime(at: number, now: number = Date.now()): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  try {
    // An older row shows its DAY. An hour alone would put an old goal at "09:00", which reads as this
    // morning.
    return date.toDateString() === new Date(now).toDateString()
      ? date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export interface Home3EmptyState {
  titleKey: string;
}

/**
 * The designed empty state of "All" (Figma 20018:112067), or `null` when the card keeps its plain line.
 *
 * Only a READY read with no rows and no status filter earns it. A running or failed read has not shown
 * that nothing is there, and an empty status-filtered list is answered by `emptyFeedMessage`, which
 * names the filter. A category shows its own page, so its empty state is that page's.
 * Shared 1:1 with iOS `ActivityMapListHome.emptyState` and Android `homeEmptyState`.
 */
export function home3EmptyState(readState: Home3ReadState, state: Home3StateFilter, rowCount: number): Home3EmptyState | null {
  if (readState !== 'ready' || state !== null || rowCount > 0) return null;
  return { titleKey: 'home.overview.emptyState.all.title' };
}
